// src/components/game/GameFrame.tsx
'use client';

import { useState, useEffect } from 'react';
import { Rating } from '@/components/common/Rating';
import Head from 'next/head';

interface GameFrameProps {
  gameId: string;
  lang?: string;
}

// 扩展 Document 接口以支持浏览器前缀
interface DocumentWithFullscreen extends Document {
  webkitFullscreenElement?: Element | null;
  mozFullScreenElement?: Element | null;
  msFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void>;
  mozCancelFullScreen?: () => Promise<void>;
  msExitFullscreen?: () => Promise<void>;
}

// 扩展 HTMLIFrameElement 接口以支持浏览器前缀
interface HTMLIFrameElementWithFullscreen extends HTMLIFrameElement {
  webkitRequestFullscreen?: () => Promise<void>;
  mozRequestFullScreen?: () => Promise<void>;
  msRequestFullscreen?: () => Promise<void>;
}

const GameFrame = ({ gameId, lang = 'en' }: GameFrameProps) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [gameUrl, setGameUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isExternal, setIsExternal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchGameUrl = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // 获取URL信息
        const response = await fetch(`/api/game-url?gameId=${gameId}&lang=${lang}`);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to fetch game URL');
        }
        
        if (data.url) {
          if (data.type === 'internal') {
            setGameUrl(data.url);
            setIsExternal(false);
          } else {
            // 外部URL需要解密
            const decryptResponse = await fetch('/api/game-url', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ encryptedUrl: data.url }),
            });
            
            if (!decryptResponse.ok) {
              throw new Error('Failed to decrypt URL');
            }
            
            const { url: decryptedUrl } = await decryptResponse.json();
            setGameUrl(decryptedUrl);
            setIsExternal(true);
          }
        }
      } catch (error) {
        console.error('Error fetching game URL:', error);
        setError((error as Error).message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchGameUrl();
  }, [gameId, lang]);

  const handleFullscreen = async () => {
    const gameFrame = document.getElementById('gameFrame') as HTMLIFrameElementWithFullscreen;
    const doc = document as DocumentWithFullscreen;
    if (!gameFrame) return;

    try {
      if (!doc.fullscreenElement && 
          !doc.webkitFullscreenElement && 
          !doc.mozFullScreenElement && 
          !doc.msFullscreenElement) {
          
        if (gameFrame.requestFullscreen) {
          await gameFrame.requestFullscreen();
        } else if (gameFrame.webkitRequestFullscreen) {
          await gameFrame.webkitRequestFullscreen();
        } else if (gameFrame.mozRequestFullScreen) {
          await gameFrame.mozRequestFullScreen();
        } else if (gameFrame.msRequestFullscreen) {
          await gameFrame.msRequestFullscreen();
        }
        setIsFullscreen(true);
      } else {
        if (doc.exitFullscreen) {
          await doc.exitFullscreen();
        } else if (doc.webkitExitFullscreen) {
          await doc.webkitExitFullscreen();
        } else if (doc.mozCancelFullScreen) {
          await doc.mozCancelFullScreen();
        } else if (doc.msExitFullscreen) {
          await doc.msExitFullscreen();
        }
        setIsFullscreen(false);
      }
    } catch (err) {
      console.error('Error attempting to enable fullscreen:', err);
    }
  };

  useEffect(() => {
    const doc = document as DocumentWithFullscreen;
    
    const handleFullscreenChange = () => {
      setIsFullscreen(
        !!(doc.fullscreenElement || 
           doc.webkitFullscreenElement || 
           doc.mozFullScreenElement || 
           doc.msFullscreenElement)
      );
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  const handleFrameLoad = () => {
    setIsLoading(false);
  };

  const handleFrameError = (e: React.SyntheticEvent<HTMLIFrameElement, Event>) => {
    console.error('Error loading iframe:', e);
    setError('Failed to load game content');
    setIsLoading(false);
  };

  return (
    <>
      <Head>
        {isExternal && <meta name="robots" content="noindex,nofollow" />}
      </Head>
      <div className="game-container w-full max-w-4xl mx-auto">
        <div className="game-controls bg-white/90 rounded-lg shadow-md mb-4 flex">
          <div className="flex-1 flex justify-end items-center p-2 space-x-6">
            <Rating gameId={gameId} />
            <button 
              onClick={handleFullscreen}
              className="p-2 rounded hover:bg-gray-200 transition-colors"
              aria-label="Toggle fullscreen"
              disabled={!!error}
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                className="w-6 h-6"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" 
                />
              </svg>
            </button>
          </div>
        </div>

        {isLoading && (
          <div className="w-full aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
          </div>
        )}

        {error && !isLoading && (
          <div className="w-full aspect-video bg-red-50 rounded-lg flex items-center justify-center">
            <div className="text-red-500">
              <p className="text-center">{error}</p>
              <button 
                onClick={() => window.location.reload()}
                className="mt-4 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {!error && !isLoading && gameUrl && (
          <iframe
            id="gameFrame"
            src={gameUrl}
            className={`w-full rounded-lg shadow-lg ${
              isFullscreen ? 'fixed inset-0 w-screen h-screen z-50' : 'aspect-video'
            }`}
            allowFullScreen
            {...(isExternal ? { rel: "nofollow" } : {})}
            loading="lazy"
            onLoad={handleFrameLoad}
            onError={handleFrameError}
          />
        )}
      </div>
    </>
  );
};

export default GameFrame;